import React, {useEffect, useRef, useState} from 'react';
import {Helmet} from 'react-helmet';
import Select from 'react-select';
import moment from 'moment';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from '../../hooks/useTranslation';
import DashboardHeader from '../../components/DashboardHeader';
import Wrapper from '../../components/Wrapper';
import {
  useAssignCustomInspectionMutation,
  useCreateCustomEquipmentTypeMutation,
  useCreateCustomInspectionMutation,
  useGetEquipmentTypeQuery,
  useGetFacilityQuery,
  useLazyGetCustomCategoriesQuery,
  useLazyGetCustomInspectionsDetailsQuery,
  useLazyGetEquipmentByTypeQuery,
  useLazyGetEquipmentQuery,
  useLazyGetFacilityByTypeQuery,
  useUpdateCustomInspectionMutation,
  useLazyGetCustomInspectionsPeriodsQuery,
} from '../../store/builder/inspections.api';
import CustomInspectionsBuildModal from '../../components/CustomInpections/BuildInspectionModal';
import {setLoader} from '../../store/builder/reducer';
import Input from '../../components/Input';
import Datepicker from '../../components/Datepicker';
import './index.scss';
import SimpleModal from '../../components/SimpleModal';
import {equipmentTypes, timeOptions} from './buildInspectionUtils';
import CreateEquipmentModal from '../../components/CustomInpections/CreateEquipmentModal';
import {toast} from 'react-toastify';
import FacilityModal from '../../components/FacilityModal';
import { setDefaultState } from '../CreateNewForm/service/createForm.slice';
import {useDebounce} from '../LogSheets/hooks';

const BuildInspection = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assignInspection = queryParams.get('assign');
  const userType =
    location.state?.userType === 'corporate' ? 'corporate' : 'admin';
  const readOnlyFromCorporate = location.state?.readOnlyFromCorporate;
  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const loaderRef = useRef(null);
  const [createCustomEquipment, setCreateCustomEquipment] = useState(false);
  const [buildInspection, setBuildInspection] = useState(false);
  const [buildEquipments, setBuildEquipments] = useState(false);
  const [buildFacilities, setBuildFacilities] = useState(false);
  const [createNewEquipment, setCreateNewEquipment] = useState(false);
  const [selectedEquipments, setSelectedEquipments] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [state, setState] = useState({
    name: { required: true, value: null, label: t('buildInspection.messages.nameRequired') },
    category_ids: { required: true, value: [], label: t('buildInspection.messages.categoryRequired') },
    period: { required: true, value: null, label: t('buildInspection.messages.periodRequired') },
    inspection_start_date: {
      required: true,
      value: null,
      label: t('buildInspection.messages.inspectionStartDateRequired'),
    },
    is_for_equipment: { required: false, value: null, label: t('buildInspection.messages.equipmentRequired') },
    equipment_type_id: {
      required: Boolean(assignInspection),
      value: null,
      label: t('buildInspection.messages.equipmentRequired'),
    }, // needed when is_for_equipment is true
    item_ids: { required: false, value: [], label: t('buildInspection.messages.selectAllOrMultipleRequired') }, // nullable when corporate is true and when is_for_equipment false
    open_days: { required: false, value: null, label: t('buildInspection.openDays') },
  });
  const [createEquipmentValue, setCreateEquipmentValue] = useState('');
  const [equipmentTypesOptions, setEquipmentTypesOptions] = useState(null);
  const [facilityTypesOptions, setFacilityTypesOptions] = useState(null);
  const [createFacilityStatus, setCreateFacilityStatus] = useState(false);
  const [agenciesReadOnly, setAgenciesReadOnly] = useState(false);
  const [equipmentTypeCategory, setEquipmentTypeCategory] = useState(null);
  const [equipmentTypeHasItems, setEquipmentTypeHasItems] = useState(true);
  const [
    getFormsList,
    { data: formsData, isFetching: formsIsFetching, isSuccess: formsIsSuccess },
  ] = useLazyGetCustomCategoriesQuery();
  const [formsListForModal, setFormsListForModal] = useState([]);
  const [formsPage, setFormsPage] = useState(1);
  
  // Equipment pagination state
  const equipmentLoaderRef = useRef(null);
  const [equipmentListForModal, setEquipmentListForModal] = useState([]);
  const [equipmentPage, setEquipmentPage] = useState(1);
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const debouncedEquipmentSearch = useDebounce(equipmentSearch, 500);
  
  // Facilities pagination state
  const facilityLoaderRef = useRef(null);
  const [facilityListForModal, setFacilityListForModal] = useState([]);
  const [facilityPage, setFacilityPage] = useState(1);
  const [canEditFormType, setCanEditFormType] = useState(true);
  const [canEditFormInfo, setCanEditFormInfo] = useState(true);
  const [canEditStartDate, setCanEditStartDate] = useState(true);

  const [
    updateInspection,
    {
      data: updateInspectionData,
      isSuccess: updateInspectionIsSuccess,
      isLoading: updateInspectionIsLoading,
    },
  ] = useUpdateCustomInspectionMutation();

  const [
    getDetails,
    {
      data: inspectionDetailsData,
      isSuccess: inspectionDetailsIsSuccess,
      isLoading: inspectionDetailsIsLoading,
    },
  ] = useLazyGetCustomInspectionsDetailsQuery();

  useEffect(() => {
    if (!params?.id) {
      return;
    }
    if (userInfo?.is_corporate && userType === 'corporate') {
      const canEdit = inspectionDetailsData?.accepted_agencies === 0;
      setCanEditFormType(canEdit)
      setCanEditStartDate(canEdit)
    } else {
      const canEdit = inspectionDetailsData?.parent_id === null && inspectionDetailsData?.agency_id !== null;
      setCanEditFormInfo(canEdit)
      setCanEditFormType(canEdit)
      setCanEditStartDate(canEdit)
    }
  }, [inspectionDetailsData]);



  const [getCustomInspectionsPeriods, {data: customInspectionsPeriodsData, isSuccess: customInspectionsPeriodsIsSuccess}] = useLazyGetCustomInspectionsPeriodsQuery();

  const [
    getEquipmentByTypes,
    {data: equipmentByTypesOptions, isSuccess: isGetEquipmentByTypesIsSuccess},
  ] = useLazyGetEquipmentByTypeQuery();
  const [getFacilityByTypes] = useLazyGetFacilityByTypeQuery();

  const [
    getEquipment,
    {data: getEquipmentData, isFetching: getEquipmentIsLoading},
  ] = useLazyGetEquipmentQuery();

  const [createCustomInspection, {isLoading: createCustomInspectionIsLoading}] =
    useCreateCustomInspectionMutation();

  const {data: equipmentTypeData, isLoading: equipmentTypeIsLoading} =
    useGetEquipmentTypeQuery();
  const {data: facilityData, isLoading: facilityIsLoading} =
    useGetFacilityQuery();
  const [assignInspectionAction, {isLoading: assignInspectionIsLoading}] =
    useAssignCustomInspectionMutation();

  const [createCustomInspectionType, {}] =
    useCreateCustomEquipmentTypeMutation();

  useEffect(() => {
    if (buildInspection) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [buildInspection]);

  useEffect(() => {
    if (isGetEquipmentByTypesIsSuccess) {
      fetchEquipmentByTypes(equipmentByTypesOptions);
    }
  }, [isGetEquipmentByTypesIsSuccess, equipmentByTypesOptions]);

  useEffect(() => {
    getCustomInspectionsPeriods();
    if (params?.id) {
      setBuildInspection(false);
      getDetails(params?.id);
    }
    if (userType === 'corporate') {
      setState(old => ({...old, publish: false}));
    }
  }, []);

  useEffect(() => {
    if (equipmentTypeCategory?.type === 'default') {
      if (getEquipmentData?.data?.length == 0) {
        setEquipmentTypeHasItems(false);
      } else {
        setEquipmentTypeHasItems(true);
      }
    } else {
      setEquipmentTypeHasItems(true);
    }
  }, [getEquipmentData, equipmentTypeCategory]);

  useEffect(() => {
    if (inspectionDetailsIsSuccess && equipmentTypesOptions !== null) {
      setEquipmentTypeCategory(old => {
        if (
          equipmentTypesOptions[1]?.options?.filter(
            el => el.value === inspectionDetailsData.equipment_type_id,
          )?.length !== 0
        ) {
          return {
            ...old,
            type: 'custom',
            value: inspectionDetailsData.equipment_type_id,
          };
        } else {
          return {
            ...old,
            type: 'default',
            value: inspectionDetailsData.equipment_type_id,
          };
        }
      });
      setState(old => ({
        ...old,
        category_ids: {
          ...old.category_ids,
          value: inspectionDetailsData.categories?.map(el => ({
            ...el,
            checked: true,
          })),
        },
        name: {...old.name, value: inspectionDetailsData.name},
        period: {...old.period, value: inspectionDetailsData.period},
        inspection_start_date: {
          ...old.inspection_start_date,
          value: moment(inspectionDetailsData.inspection_start_date).format(
            'Y-MM-DD',
          ),
        },
        is_for_equipment: {
          ...old.is_for_equipment,
          value: inspectionDetailsData.is_for_equipment ? true : false,
        },
        open_days: {
          ...old.open_days,
          value: inspectionDetailsData.open_days,
        },
      }));
      if (!assignInspection) {
        setState(old => ({
          ...old,
          equipment_type_id: {
            ...old.equipment_type_id,
            value: inspectionDetailsData.equipment_type_id,
          },
          equipment_type: {
            ...old.equipment_type,
            value:
              equipmentTypesOptions[0]?.options?.filter(
                el => el.value === inspectionDetailsData.equipment_type_id,
              )?.length !== 0
                ? equipmentTypesOptions[0]?.options?.filter(
                    el => el.value === inspectionDetailsData.equipment_type_id,
                  )[0]?.label
                : equipmentTypesOptions[1]?.options?.filter(
                      el =>
                        el.value === inspectionDetailsData.equipment_type_id,
                    )?.length !== 0
                  ? equipmentTypesOptions[1]?.options?.filter(
                      el =>
                        el.value === inspectionDetailsData.equipment_type_id,
                    )[0]?.label
                  : null,
          },
          equipment_selection: {
            ...old.equipment_selection,
            value: inspectionDetailsData.is_for_equipment
              ? inspectionDetailsData?.equipment_type_id &&
                !Boolean(inspectionDetailsData?.items?.length)
                ? 'all'
                : 'multiple'
              : !Boolean(inspectionDetailsData?.items?.length)
                ? 'all'
                : 'multiple',
          },
        }));
      }
      // if (userType === 'corporate') {
      //   setState(old => ({...old, publish: inspectionDetailsData?.publish}))
      // }
      if (inspectionDetailsData.is_for_equipment) {
        setSelectedEquipments(
          inspectionDetailsData?.items
            ? inspectionDetailsData?.items?.map(el => ({
                ...el,
                title: el.name,
                checked: true,
              }))
            : null,
        );
      } else {
        setSelectedFacilities(
          inspectionDetailsData?.items
            ? inspectionDetailsData?.items?.map(el => ({
                ...el,
                title: el.name,
                checked: true,
              }))
            : null,
        );
      }

      if (userType === 'corporate') {
        if (inspectionDetailsData?.accepted_agencies !== 0) {
          setAgenciesReadOnly(true);
        } else {
          setAgenciesReadOnly(false);
        }
      }
    }
  }, [
    inspectionDetailsData,
    inspectionDetailsIsSuccess,
    equipmentTypesOptions,
  ]);

  useEffect(() => {
    dispatch(
      setLoader(
        getEquipmentIsLoading ||
          createCustomInspectionIsLoading ||
          inspectionDetailsIsLoading ||
          updateInspectionIsLoading ||
          assignInspectionIsLoading,
      ),
    );
  }, [
    getEquipmentIsLoading,
    createCustomInspectionIsLoading,
    inspectionDetailsIsLoading,
    updateInspectionIsLoading,
    assignInspectionIsLoading,
  ]);

  // useEffect(() => {
  //   setState((old) => ({...old, item_ids: {...old.item_ids, required: userType !== 'corporate'}}))
  // }, [state.is_for_equipment])

  const getEquipmentsByType = () => {
    if (
      state.equipment_type_id.value !== null &&
      state.equipment_type_id.value !== undefined
    ) {
      // Reset page to 1 to trigger fetch with pagination
      setEquipmentPage(1);
    }
  };

  useEffect(() => {
    dispatch(setLoader(true));
    getEquipmentByTypes();
    fetchFacilityByTypes();
  }, []);

  useEffect(() => {
    getFormsList({page: formsPage, is_corporate: userType === 'corporate'})
      .unwrap()
      .finally(() => {
        dispatch(setLoader(false));
      });
  }, [formsPage, userType]);

  useEffect(() => {
    if (formsIsSuccess && formsData) {
      setFormsListForModal(prevItems => [...prevItems, ...formsData?.data]);
    }
  }, [formsData, formsIsSuccess]);
  
  // Fetch equipment with pagination
  useEffect(() => {
    if (state?.equipment_type_id.value && equipmentPage > 0) {
      getEquipment({
        equipment_type_id: state.equipment_type_id.value,
        page: equipmentPage,
        search: debouncedEquipmentSearch || null,
      });
    }
  }, [equipmentPage, state?.equipment_type_id.value, debouncedEquipmentSearch, buildEquipments]);
  
  // Accumulate equipment data
  useEffect(() => {
    if (getEquipmentData?.data) {
      if (equipmentPage === 1) {
        setEquipmentListForModal(getEquipmentData?.data);
      } else {
        setEquipmentListForModal(prevItems => [...prevItems, ...getEquipmentData?.data]);
      }
    }
  }, [getEquipmentData, equipmentPage, buildEquipments]);
  
  
  // Reset equipment pagination when equipment type changes
  useEffect(() => {
    setEquipmentPage(1);
    setEquipmentListForModal([]);
  }, [state?.equipment_type_id.value]);

  // Reset equipment pagination when search changes
  useEffect(() => {
    if (buildEquipments) {
      setEquipmentPage(1);
      setEquipmentListForModal([]);
    }
  }, [debouncedEquipmentSearch]);

  // Reset search when modal opens
  useEffect(() => {
    if (!buildEquipments) {
      setEquipmentSearch('');
      setEquipmentPage(1);
      setEquipmentListForModal([]);
    }
  }, [buildEquipments]);

  const handleInput = e => {
    setState(old => ({
      ...old,
      [e.target.name]: {...old[e.target.name], value: e.target.value},
    }));
  };

  const navigateByUserType = () => {
    if (userType === 'corporate') {
      navigate('/corporate/custom-inspections', {
        state: {userType: 'corporate'},
      });
    } else {
      navigate('/custom-inspections');
    }
  };

  const handleSubmit = () => {
    if (validateFields()) {
      let result = {
        name: state.name.value,
        category_ids: state.category_ids.value?.map(el => el.id),
        period: state.period.value,
        corporate: userType === 'corporate',
        inspection_start_date: state.inspection_start_date.value,
        is_for_equipment: state.is_for_equipment.value,
        equipment_type_id: state.equipment_type_id.value,
        item_ids: state.is_for_equipment.value
          ? selectedEquipments == null
            ? null
            : selectedEquipments?.map(el => el.id)
          : selectedFacilities == null
            ? null
            : selectedFacilities?.map(el => el.id),
      };

      if (state.period.value === 'custom') {
        result.open_days = state.open_days.value;
      }
      
      if (userType === 'corporate' && !params?.id) {
        result.publish = state.publish;
      }
      if (assignInspection) {
        assignInspectionAction({
          body: {
            equipment_type_id: result?.equipment_type_id,
            item_ids: result?.item_ids,
          },
          id: params?.id,
        })
          .unwrap()
        .then(() => {
          navigateByUserType();
          toast.success(t('buildInspection.messages.inspectionAssignedSuccess'));
        })
        .catch(err => {
          toast.error(err.data.message);
        });
      } else if (params?.id) {
        updateInspection({body: result, id: params?.id})
          .unwrap()
          .then(() => {
            navigateByUserType();
            toast.success(t('buildInspection.messages.inspectionUpdatedSuccess'));
          })
          .catch(err => {
            toast.error(err.data.message);
          });
      } else {
        createCustomInspection(result)
          .unwrap()
          .then(() => {
            navigateByUserType();
            toast.success(t('buildInspection.messages.inspectionCreatedSuccess'));
          })
          .catch(err => {
            toast.error(err.data.message);
          });
      }
    }
  };

  const fetchEquipmentByTypes = res => {
    let {ata_types, custom_types} = res;
    let newOptions = [
      {
        label: t('buildInspection.equipmentTypes.defaultEquipmentTypes'),
        options: ata_types?.map(el => ({
          value: el.id,
          label: el.title,
          type: 'default',
        })),
      },
      {
        label: t('buildInspection.equipmentTypes.customTypes'),
        options: custom_types?.map(el => ({
          value: el?.id,
          label: el?.title,
          type: 'custom',
        })),
      },
    ];

    setEquipmentTypesOptions([...newOptions]);
  };
  const fetchFacilityByTypes = async () => {
    await getFacilityByTypes()
      .unwrap()
      .then(res => setFacilityTypesOptions(res));
  };

  const createEquipmentType = () => {
    dispatch(setLoader(true));
    createCustomInspectionType({title: createEquipmentValue})
      .unwrap()
      .then(async () => {
        await getEquipmentByTypes();
        setCreateEquipmentValue('');
        setCreateCustomEquipment(false);
      })
      .finally(() => {
        dispatch(setLoader(false));
      });
  };

  const validateFields = () => {
    let errors = [];
    Object.keys(state).map(key => {
      if (!state['item_ids']) {
        if (state[key].required) {
          if (Array.isArray(state[key].value) && !state[key].value.length) {
            errors.push(state[key].label);
          } else {
            if (!state[key].value) {
              errors.push(state[key].label);
            }
          }
        }
      }
    });

    if (userType !== 'corporate') {
      if (state.is_for_equipment.value) {
        if (selectedEquipments !== null && selectedEquipments?.length == 0) {
          errors.push(state['item_ids'].label);
        }
      } else {
        if (selectedFacilities !== null && selectedFacilities?.length == 0) {
          errors.push(state['item_ids'].label);
        }
      }
    }

    if (errors.length) {
      errors.map(item => {
        toast.error(`${item} ${item === t('buildInspection.messages.itemsRequired') ? 'are' : 'is'} required!`);
      });
      return false;
    }
    return true;
  };

  return (
    <Wrapper>
      {buildInspection && (
        <CustomInspectionsBuildModal
          exit={() => {
            setBuildInspection(false);
          }}
          createAction={() => {
            dispatch(setDefaultState());
            if (userType === 'corporate') {
              navigate('/corporate/forms/create-form', {
                state: {page: 'build-inspection', userType: 'corporate' },
              });
            } else {
              navigate('/forms/create-form', {
                state: {page: 'build-inspection'},
              });
            }
          }}
          items={formsListForModal?.map(el => ({...el, checked: false}))}
          selectedItems={state.category_ids.value}
          setSelectedItems={v => {
            setState(old => ({
              ...old,
              category_ids: {...old.category_ids, value: v},
            }));
          }}
          title={t('buildInspection.modals.selectOrCreateInspectionForms')}
          itemTitle={t('customInspectionsBuildModal.itemTitles.forms')}
          actionTitle={t('customInspectionsBuildModal.itemTitles.forms')}
          newTitle={t('customInspectionsBuildModal.itemTitles.form')}
          type={'form'}
          loaderRef={loaderRef}
          isFetching={formsIsFetching}
          setPage={setFormsPage}
          maxPage={formsData?.pages}
          currentPage={formsPage}
        />
      )}
      {buildEquipments && (
        <CustomInspectionsBuildModal
          exit={() => {
            setBuildEquipments(false);
            setEquipmentSearch('');
            setEquipmentPage(1);
            setEquipmentListForModal([]);
          }}
          createAction={() => {
            // setBuildEquipments(false);
            setCreateNewEquipment(true);
          }}
          items={equipmentListForModal}
          selectedItems={selectedEquipments}
          setSelectedItems={setSelectedEquipments}
          title={t('buildInspection.modals.selectEquipment')}
          itemTitle={t('buildInspection.equipment')}
          actionTitle={t('buildInspection.equipment')}
          newTitle={t('buildInspection.equipment')}
          type={'equipment'}
          ifShowCreate={equipmentTypeCategory?.type === 'custom'}
          equipmentTypeCategory={
            equipmentTypeCategory || {value: state?.equipment_type_id.value}
          }
          getEquipmentsActions={getEquipmentsByType}
          loaderRef={equipmentLoaderRef}
          isFetching={getEquipmentIsLoading}
          setPage={setEquipmentPage}
          maxPage={getEquipmentData?.pages}
          currentPage={equipmentPage}
          searchValue={equipmentSearch}
          onSearchChange={setEquipmentSearch}
        />
      )}
      {buildFacilities && (
        <CustomInspectionsBuildModal
          exit={() => setBuildFacilities(false)}
          items={facilityTypesOptions}
          selectedItems={selectedFacilities}
          setSelectedItems={setSelectedFacilities}
          title={t('buildInspection.modals.selectFacility')}
          itemTitle={t('buildInspection.facilities')}
          actionTitle={t('buildInspection.facility')}
          newTitle={t('buildInspection.facility')}
          type={'facility'}
          ifShowCreate={false}
        />
      )}
      {createCustomEquipment && (
        <SimpleModal
          title={t('buildInspection.customEquipmentType')}
          exit={() => {
            setCreateCustomEquipment(false);
          }}
          submitAction={() => createEquipmentType()}
          submitTitle={t('buildInspection.create')}
        >
          <Input
            customStyles={{margin: '0', width: '100%'}}
            titleStyles={{fontSize: '16px', color: '#565F67'}}
            id='equipment_type_name'
            name='equipment_type_name'
            placeholder={t('buildInspection.enterEquipmentTypeName')}
            label={t('buildInspection.equipmentTypeName')}
            type={'text'}
            onChange={e => setCreateEquipmentValue(e.target.value)}
          />
        </SimpleModal>
      )}
      {createNewEquipment && (
        <CreateEquipmentModal
          exit={() => setCreateNewEquipment(false)}
          equipmentTypeData={equipmentTypeData}
          facilityData={facilityData}
          getEquipmentByTypes={getEquipmentByTypes}
          getEquipmentsActions={getEquipmentsByType}
          equipmentTypeCategory={equipmentTypeCategory}
        />
      )}
      {createFacilityStatus && (
        <FacilityModal
          setModal={setCreateFacilityStatus}
          getData={fetchFacilityByTypes}
        />
      )}

      <Helmet>
        <title>{t('buildInspection.pageTitle')}</title>
      </Helmet>
      <DashboardHeader
        title={t('buildInspection.title')}
        showBtn={true}
        type='build-form'
        action={() => navigate(-1)}
      />
      <div className='build'>
        <div className='build__content'>
          <div className='build__builder'>
            <div className='build__block'>
              <div className='build__block-header'>
                <p>{t('buildInspection.forms')}</p>
                {canEditFormInfo && (
                  <p
                    onClick={() => setBuildInspection(true)}
                    className='text-button primary-button'
                  >
                    {t('buildInspection.edit')}
                  </p>
                )}
              </div>
              <div className='build__block-forms'>
                {state?.category_ids?.value?.map((item, id) => (
                  <React.Fragment key={item?.id}>
                    <div
                      className={`build__block-form ${!canEditFormInfo ? 'disabled' : ''}`}
                    >
                      <p>{item?.name}</p>
                    </div>
                    {id < state.category_ids.value?.length - 1 && (
                      <div className='build__block-divider' />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            {/*{*/}
            {/*  state?.category_ids?.value?.length ? (*/}
            <>
              <div className='build__block'>
                <Input
                  customStyles={{margin: '0', width: '100%'}}
                  titleStyles={{fontSize: '16px', color: '#565F67'}}
                  id='name'
                  name='name'
                  placeholder={t('buildInspection.enterInspectionName')}
                  label={t('buildInspection.customInspectionName')}
                  type={'text'}
                  defaultValue={state?.name?.value}
                  onChange={handleInput}
                  readOnly={
                    !canEditFormInfo
                  }
                />
                <div className='build__block-item'>
                  <span className='build__block-item__label'>
                    {t('buildInspection.inspectionPeriod')}
                  </span>
                  <Select
                    options={customInspectionsPeriodsData ? Object.entries(customInspectionsPeriodsData).map(([key, value]) => ({
                      value: key,
                      label: value,
                    })) : []}
                    placeholder={t('buildInspection.selectPeriod')}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                        borderRadius: 8,
                        width: '100%',
                        height: 43,
                        fontSize: 14,
                        backgroundColor: '#F4F5F6',
                        color: '#718096',
                      }),
                    }}
                    isDisabled={
                      !canEditFormInfo
                    }
                    value={
                      customInspectionsPeriodsData ? Object.entries(customInspectionsPeriodsData).map(([key, value]) => ({
                        value: key,
                        label: value,
                      })).filter(
                        el => el.value === state?.period?.value,
                      )[0] : null
                    }
                    onChange={selected => {
                      setState(old => ({
                        ...old,
                        period: {...old.period, value: selected.value},
                      }));
                    }}
                  />
                </div>
                {
                  state?.period?.value === 'custom' && (
                   <div className='build__block-item'>
                  <span className='build__block-item__label'>
                    {t('buildInspection.openDays')}
                  </span>
                  <Input
                    type='number'
                    placeholder={t('buildInspection.enterOpenDays')}
                    value={state?.open_days?.value}
                    customStyles={{
                      width: '100%',
                    }}
                    onChange={e =>
                      setState(old => ({
                        ...old,
                        open_days: {...old.open_days, value: e.target.value},
                      }))
                    }
                    readOnly={!canEditFormInfo}
                    />
                </div> 
                  )
                }
              </div>
              <div className='build__block'>
                <div className='build__block-item'>
                  <p className='build__block-item__label'>{t('buildInspection.startDate')}</p>
                  <Datepicker
                    readOnly={
                      !canEditStartDate
                    }
                    width={'100%'}
                    value={state?.inspection_start_date?.value}
                    setValue={e =>
                      setState(old => ({
                        ...old,
                        inspection_start_date: {
                          ...old.inspection_start_date,
                          value:
                            e !== null && e !== 'Invalid date'
                              ? moment(e).format('Y-MM-DD')
                              : null,
                        },
                      }))
                    }
                  />
                </div>
              </div>
              <div className='build__block'>
                <div className='build__block-item'>
                  <p className='build__block-item__label'>
                    {t('buildInspection.whereAddingInspection')}
                  </p>
                  <label className='build-radio'>
                    <input
                      type='radio'
                      id='inspection_type'
                      name='inspection_type'
                      value='facility'
                      onChange={() => {
                        setState(old => ({
                          ...old,
                          is_for_equipment: {
                            ...old.is_for_equipment,
                            value: false,
                          },
                          equipment_type_id: {
                            ...old.equipment_type_id,
                            value: null,
                          },
                          equipment_type: {
                            ...old.equipment_type,
                            value: null,
                          },
                          equipment_selection: {
                            ...old.equipment_selection,
                            value: null,
                          },
                        }));
                      }}
                      checked={state.is_for_equipment.value === false}
                      disabled={
                        !canEditFormType
                      }
                    />
                    {t('buildInspection.facility')}
                    <span className={`checkmark`}></span>
                  </label>
                  <label className='build-radio'>
                    <input
                      type='radio'
                      id='inspection_type'
                      name='inspection_type'
                      value='equipment'
                      onChange={() => {
                        setState(old => ({
                          ...old,
                          is_for_equipment: {
                            ...old.is_for_equipment,
                            value: true,
                          },
                          equipment_type_id: {
                            ...old.equipment_type_id,
                            value: null,
                          },
                          equipment_type: {
                            ...old.equipment_type,
                            value: null,
                          },
                          equipment_selection: {
                            ...old.equipment_selection,
                            value: null,
                          },
                        }));
                      }}
                      checked={state.is_for_equipment.value}
                      disabled={
                        !canEditFormType
                      }
                    />
                    {t('buildInspection.equipment')}
                    <span className={`checkmark`}></span>
                  </label>
                </div>
              </div>
              {state?.is_for_equipment.value !== null &&
                userType !== 'corporate' && (
                  <div className='build__block'>
                    <div className='build__block-item'>
                      <div className='build__block-header'>
                        <p>
                          {state?.is_for_equipment.value
                            ? t('buildInspection.equipmentType')
                            : t('buildInspection.facilities')}
                        </p>
                        {canEditFormType && (
                          <p
                          onClick={() => {
                            state?.is_for_equipment.value
                              ? setCreateCustomEquipment(true)
                              : setCreateFacilityStatus(true);
                          }}
                          className='text-button primary-button'
                        >
                          {state?.is_for_equipment.value
                            ? t('buildInspection.createEquipmentType')
                            : t('buildInspection.createFacility')}
                        </p>
                        )}
                      </div>
                      {state?.is_for_equipment.value &&
                      equipmentTypesOptions?.length > 0 && (
                          <Select
                            key={JSON.stringify(equipmentTypesOptions)}
                            options={equipmentTypesOptions}
                            placeholder={t('buildInspection.selectEquipmentType')}
                            styles={{
                              control: (baseStyles, state) => ({
                                ...baseStyles,
                                borderColor: state.isFocused
                                  ? '#E9EAEB'
                                  : '#E9EAEB',
                                borderRadius: 8,
                                width: '100%',
                                height: 43,
                                fontSize: 14,
                                backgroundColor: '#F4F5F6',
                                color: '#718096',
                              }),
                            }}
                            menuPlacement='top'
                            onChange={selected => {
                              setState(old => ({
                                ...old,
                                equipment_type_id: {
                                  ...old.equipment_type_id,
                                  value: selected.value,
                                },
                                equipment_type: {
                                  ...old.equipment_type,
                                  value: selected.label,
                                },
                                equipment_selection: {
                                  ...old.equipment_selection,
                                  value: null,
                                },
                              }));
                              setSelectedEquipments([]);
                              setSelectedFacilities([]);
                              setEquipmentTypeCategory(selected);
                            }}
                            value={{
                              label: state.equipment_type?.value,
                              value: state.equipment_type_id?.value,
                            }}
                          />
                        )}
                    </div>
                    {state?.is_for_equipment?.value ? (
                      state?.is_for_equipment?.value &&
                      state.equipment_type_id?.value &&
                      equipmentTypeHasItems ? (
                        <div className='build__block-item'>
                          <div className='build__block-header'>
                            <p className='build__block-item__label'>
                              {state?.is_for_equipment?.value
                                ? t('buildInspection.equipment')
                                : ''}
                            </p>
                            {inspectionDetailsData?.parent_id !== null && (selectedEquipments?.length ||
                            selectedFacilities?.length ||
                            state?.equipment_selection?.value === 'multiple' ? (
                              <p
                                onClick={() => {
                                  state?.is_for_equipment.value
                                    ? setBuildEquipments(true)
                                    : setBuildFacilities(true);
                                }}
                                className='text-button primary-button'
                              >
                                {t('buildInspection.edit')}
                              </p>
                            ) : null)}
                          </div>
                          <div className='build__block-dual'>
                            <label className='build-radio'>
                              <input
                                type='radio'
                                id='equipment__choose'
                                name='equipment__choose'
                                value='all'
                                onChange={e => {
                                  setState(old => ({
                                    ...old,
                                    equipment_selection: {
                                      ...old.equipment_selection,
                                      value: e.target.value,
                                    },
                                  }));
                                  setSelectedEquipments(null);
                                  setSelectedFacilities(null);
                                }}
                                checked={
                                  state?.equipment_selection?.value === 'all'
                                }
                              />
                              {state.is_for_equipment.value ? t('buildInspection.selectAllEquipment') : t('buildInspection.selectAllFacilities')}
                              <span className={`checkmark`}></span>
                            </label>
                            <label className='build-radio'>
                              <input
                                type='radio'
                                id='equipment__choose'
                                name='equipment__choose'
                                value='multiple'
                                onChange={e => {
                                  setState(old => ({
                                    ...old,
                                    equipment_selection: {
                                      ...old.equipment_selection,
                                      value: e.target.value,
                                    },
                                  }));
                                  state.is_for_equipment.value
                                    ? setBuildEquipments(true)
                                    : setBuildFacilities(true);
                                  setSelectedEquipments([]);
                                  setSelectedFacilities([]);
                                }}
                                checked={
                                  state?.equipment_selection?.value ===
                                  'multiple'
                                }
                              />
                              {t('buildInspection.selectMultiple')}
                              <span className={`checkmark`}></span>
                            </label>
                          </div>
                          {selectedEquipments?.length ||
                          selectedFacilities?.length ? (
                            <div className='build__block-forms'>
                              {state?.is_for_equipment?.value
                                ? selectedEquipments?.map((item, id) => (
                                    <React.Fragment key={item?.id}>
                                      <div className='build__block-form'>
                                        <p>{item?.title}</p>
                                      </div>
                                      {id < selectedEquipments?.length - 1 && (
                                        <div className='build__block-divider' />
                                      )}
                                    </React.Fragment>
                                  ))
                                : selectedFacilities?.map((item, id) => (
                                    <React.Fragment key={item?.id}>
                                      <div className='build__block-form'>
                                        <p>{item?.name}</p>
                                      </div>
                                      {id < selectedFacilities?.length - 1 && (
                                        <div className='build__block-divider' />
                                      )}
                                    </React.Fragment>
                                  ))}
                            </div>
                          ) : null}
                        </div>
                      ) : !equipmentTypeHasItems ? (
                        <div className='modal__regions-warning builder'>
                          <img src='/assets/icons/warning.svg' alt='info' />
                          <p>
                            {t('buildInspection.warningNoEquipment')}
                          </p>
                        </div>
                      ) : null
                    ) : (
                      <div className='build__block-item'>
                        <div className='build__block-header'>
                          <p className='build__block-item__label'>
                            {state?.is_for_equipment?.value ? t('buildInspection.equipment') : ''}
                          </p>
                          {canEditFormInfo && (
                            selectedEquipments?.length ||
                            selectedFacilities?.length ||
                            state?.equipment_selection?.value === 'multiple' ? (
                            <p
                              onClick={() => {
                                state?.is_for_equipment?.value
                                  ? setBuildEquipments(true)
                                  : setBuildFacilities(true);
                              }}
                              className='text-button primary-button'
                            >
                              {t('buildInspection.edit')}
                            </p>
                            ) : null
                          )}
                        </div>
                        <div className='build__block-dual'>
                          <label className='build-radio'>
                            <input
                              type='radio'
                              id='equipment__choose'
                              name='equipment__choose'
                              value='all'
                              onChange={e => {
                                setState(old => ({
                                  ...old,
                                  equipment_selection: {
                                    ...old.equipment_selection,
                                    value: e.target.value,
                                  },
                                }));
                                setSelectedEquipments(null);
                                setSelectedFacilities(null);
                              }}
                              checked={
                                state?.equipment_selection?.value === 'all'
                              }
                            />
                            {state.is_for_equipment.value ? t('buildInspection.selectAllEquipment') : t('buildInspection.selectAllFacilities')}
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='build-radio'>
                            <input
                              type='radio'
                              id='equipment__choose'
                              name='equipment__choose'
                              value='multiple'
                              onChange={e => {
                                setState(old => ({
                                  ...old,
                                  equipment_selection: {
                                    ...old.equipment_selection,
                                    value: e.target.value,
                                  },
                                }));
                                state?.is_for_equipment?.value
                                  ? setBuildEquipments(true)
                                  : setBuildFacilities(true);
                                setSelectedEquipments([]);
                                setSelectedFacilities([]);
                              }}
                              checked={
                                state?.equipment_selection?.value === 'multiple'
                              }
                            />
                            {t('buildInspection.selectMultiple')}
                            <span className={`checkmark`}></span>
                          </label>
                        </div>
                        {selectedEquipments?.length ||
                        selectedFacilities?.length ? (
                          <div className='build__block-forms'>
                            {state?.is_for_equipment?.value
                              ? selectedEquipments?.map(item => (
                                  <div
                                    className='build__block-form'
                                    key={item?.title}
                                  >
                                    <p>{item?.title}</p>
                                  </div>
                                ))
                              : selectedFacilities?.map(item => (
                                  <div
                                    className='build__block-form'
                                    key={item?.name}
                                  >
                                    <p>{item?.name}</p>
                                  </div>
                                ))}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                )}
              {userType === 'corporate' && !params?.id && (
                <div className='auth__checkbox'>
                  <input
                    type='checkbox'
                    id='publish'
                    defaultValue={state?.publish}
                    onClick={e => {
                      setState(old => ({
                        ...old,
                        publish: e.target.checked,
                      }));
                    }}
                  />
                  <label htmlFor='publish'>{t('buildInspection.publishWithFBOs')}</label>
                </div>
              )}
              {
                (
                    <button className='equipment__button' onClick={handleSubmit}>
                  {assignInspection
                    ? t('buildInspection.assign')
                    : params?.id
                      ? t('buildInspection.updateInspection')
                    : t('buildInspection.createInspection')}
              </button>)
              }
            </>
            {/*  ) : null*/}
            {/*}*/}
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default BuildInspection;
